import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, type MeHistoryItem, type MeHistoryResponse } from '@/lib/api';
import { FishBadge } from '@/components/fish-badge';
import type { Grade } from '@fishymeter/types';

export function MyCatchesPage() {
  const history = useQuery({
    queryKey: ['my-history'],
    queryFn: () => api.getMyHistory(),
  });

  return (
    <div className="mx-auto max-w-3xl px-4 py-12 sm:px-6 lg:px-8">
      <h1 className="text-3xl font-bold tracking-tight">My catches</h1>
      <p className="mt-1 text-sm text-muted-foreground">
        Everything you've metered. Toggle public/private or delete.
      </p>

      {history.isLoading && (
        <div className="mt-12 text-center text-sm text-muted-foreground">Loading…</div>
      )}
      {history.error && (
        <div className="mt-12 text-center text-sm text-destructive">
          Couldn't load your history. {(history.error as Error).message}
        </div>
      )}
      {history.data?.items.length === 0 && (
        <div className="mt-12 rounded-lg border border-border bg-card p-8 text-center">
          <p className="text-sm text-muted-foreground">No catches yet.</p>
          <Link to="/" className="mt-3 inline-block text-sm text-primary underline">
            Score something →
          </Link>
        </div>
      )}
      {history.data && history.data.items.length > 0 && (
        <ul className="mt-8 space-y-3">
          {history.data.items.map((item) => (
            <CatchRow key={item.analysis_id} item={item} />
          ))}
        </ul>
      )}
    </div>
  );
}

function CatchRow({ item }: { item: MeHistoryItem }) {
  const queryClient = useQueryClient();
  const [confirmingDelete, setConfirmingDelete] = useState(false);

  const togglePrivacy = useMutation({
    mutationFn: () => api.toggleCatchPrivacy(item.analysis_id, !item.is_public),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['my-history'] }),
  });

  const remove = useMutation({
    mutationFn: () => api.deleteCatch(item.analysis_id),
    onSuccess: () => {
      queryClient.setQueryData<MeHistoryResponse>(['my-history'], (old) => {
        if (!old) return old;
        return { ...old, items: old.items.filter((i) => i.analysis_id !== item.analysis_id) };
      });
    },
  });

  return (
    <li className="flex items-start justify-between gap-4 rounded-lg border border-border bg-card p-4 shadow-sm">
      <div className="flex-1 min-w-0">
        {item.grade && (
          <FishBadge grade={item.grade as Grade} size="sm" showGrade={false} />
        )}
        <h3 className="mt-2 font-semibold truncate">
          <Link to={`/c/${item.analysis_id}`} className="hover:text-primary hover:underline">
            {item.title ?? 'Untitled'}
          </Link>
        </h3>
        <p className="mt-1 text-xs text-muted-foreground">
          {item.is_public ? 'Public' : 'Private'} · {new Date(item.created_at).toLocaleDateString()}
          {item.overall_score !== undefined && ` · score ${item.overall_score}`}
        </p>
      </div>
      <div className="flex flex-col items-end gap-2 text-xs">
        <Link
          to={`/c/${item.analysis_id}`}
          className="rounded border border-border bg-background px-2 py-1 hover:bg-muted"
        >
          View
        </Link>
        <button
          type="button"
          onClick={() => togglePrivacy.mutate()}
          disabled={togglePrivacy.isPending}
          className="rounded border border-border bg-background px-2 py-1 hover:bg-muted disabled:opacity-60"
        >
          {item.is_public ? 'Make private' : 'Make public'}
        </button>
        {confirmingDelete ? (
          <div className="flex items-center gap-1">
            <button
              type="button"
              onClick={() => remove.mutate()}
              disabled={remove.isPending}
              className="rounded bg-destructive px-2 py-1 text-destructive-foreground hover:bg-destructive/90 disabled:opacity-60"
            >
              Confirm
            </button>
            <button
              type="button"
              onClick={() => setConfirmingDelete(false)}
              className="rounded border border-border bg-background px-2 py-1 hover:bg-muted"
            >
              Cancel
            </button>
          </div>
        ) : (
          <button
            type="button"
            onClick={() => setConfirmingDelete(true)}
            className="rounded border border-border bg-background px-2 py-1 text-destructive hover:bg-muted"
          >
            Delete
          </button>
        )}
      </div>
    </li>
  );
}

